/**
 * Pictures for flat items (banners, flags, signs), kept on the device until milestone 3 stores them in
 * R2. Each is checked, made no bigger than a phone can draw (4096 px on its long side), and kept under
 * its SHA-256, the name it will have in the file store.
 */

import type { ImageRef } from '@mawkeb/shared/layout';

const DB = 'venue-images';
const STORE = 'images';
const MAX_PX = 4096;
const MAX_BYTES = 40 * 1024 * 1024;
const TYPES = ['image/png', 'image/jpeg', 'image/webp'];

export class ImageProblem extends Error {
  override name = 'ImageProblem';
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB, 1);
    req.onupgradeneeded = () => req.result.createObjectStore(STORE);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx<T>(mode: IDBTransactionMode, run: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await open();
  return new Promise((resolve, reject) => {
    const req = run(db.transaction(STORE, mode).objectStore(STORE));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function sha256(blob: Blob): Promise<string> {
  const hash = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return [...new Uint8Array(hash)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Checks a picture someone chose, shrinks it if it is huge, and keeps it. */
export async function keepImage(file: File): Promise<ImageRef> {
  if (!TYPES.includes(file.type)) throw new ImageProblem('That file isn’t a picture the app can use. Save it as PNG, JPG or WebP and try again.');
  if (file.size > MAX_BYTES) throw new ImageProblem('That picture is over 40 MB. Save a smaller copy (4096 pixels across is plenty) and try again.');
  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new ImageProblem('That picture couldn’t be opened. Save it again as PNG or JPG and try once more.');
  }
  const scale = Math.min(1, MAX_PX / Math.max(bitmap.width, bitmap.height));
  let blob: Blob = file;
  const width = Math.round(bitmap.width * scale);
  const height = Math.round(bitmap.height * scale);
  if (scale < 1) {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, width, height);
    blob = await new Promise<Blob>((resolve, reject) => canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('toBlob'))), file.type === 'image/png' ? 'image/png' : 'image/jpeg', 0.92));
  }
  bitmap.close();
  const key = await sha256(blob);
  await tx('readwrite', (s) => s.put(blob, key));
  return { key, name: file.name, width, height };
}

const urls = new Map<string, Promise<string | null>>();

/** A URL for a kept picture, or null if this device doesn't have it. */
export function imageUrl(key: string): Promise<string | null> {
  let u = urls.get(key);
  if (!u) {
    u = tx<Blob | undefined>('readonly', (s) => s.get(key) as IDBRequest<Blob | undefined>)
      .then((blob) => (blob ? URL.createObjectURL(blob) : null))
      .catch(() => null);
    urls.set(key, u);
  }
  return u;
}
